/**
 * Klartid — API-kontraktet. Varje endpoint ar en typad konstant som bade
 * klient och server importerar. Ingen implementation.
 *
 * FALTEN PA EN RUTT
 *   method/path     :company_id, :account_id, :object_key ar parametrar;
 *                   :object_key ar URL-kodad (nyckeln bar snedstreck).
 *   auth            'public' | 'account' (AccountProof) | 'company'
 *                   (LicenseProof FOR FIRMAN I SOKVAGEN) | 'cron'
 *                   (CRON_SECRET_HEADER mot en Worker-hemlighet; ingen
 *                   anvandare, sokvagen alltid under /api/internal/).
 *                   ROLLEN LASES ALLTID UR D1: servern slar upp
 *                   membership(company_id, account_id) OCH
 *                   subscription.status vid VARJE anrop med auth
 *                   'company', aven lasande; DEN rollen avgor
 *                   roles-kontrollen och ClientView. LicenseClaims ar bara
 *                   klientens offline-kopia. Saknat medlemskap: FORBIDDEN.
 *   roles           Roller som far anropa; tom vid public/account/cron.
 *   requires_owner  true = bara firmans agare (beslut 14). Ett anrop fran en
 *                   ANNAN manager avvisas med OWNER_REQUIRED, aldrig med
 *                   FORBIDDEN: rollen racker till — det ar agarskapet som
 *                   saknas, och texten ska saga just det. FORBIDDEN
 *                   reserveras for saknat medlemskap och for en roll som
 *                   inte far gora saken alls.
 *   idempotency     'safe' (BARA GET) | 'op_id' (X-Op-Id MASTE sattas;
 *                   omtag med samma op_id, konto, SOKVAG och kropp ger
 *                   samma svar ur account_op_log — kanoniseringen star i
 *                   schema/d1.sql) | 'per_op' (BARA SYNC_PUSH: op_id per
 *                   operation i kroppen, mot sync_op_log) |
 *                   'unsafe_no_op_id' (ofarlig att upprepa, engangs till
 *                   sin natur, eller idempotent i egen tabell).
 *   locked_mode     'allowed' | 'blocked' (svarar SUBSCRIPTION_LOCKED).
 *                   Beslut 11: allt syns och kan exporteras, inget nytt
 *                   skapas eller andras; export ligger ALDRIG bakom
 *                   betalvagg. Bara statusen 'locked' blockerar — trialing,
 *                   active och past_due ar skrivbara. Statusen lases ur D1,
 *                   aldrig ur licensbevisets kopia.
 *   request_in      'none' | 'query' (EXAKT faltnamnen, t.ex.
 *                   ?from_date=2026-09-01&to_date=2026-09-30) | 'body'.
 *                   En GET har aldrig 'body'.
 *
 * Bevis: Authorization: Bearer <token>. Saknat/ogiltigt: UNAUTHENTICATED;
 * utgangen: LICENSE_EXPIRED. Beviset fornyas tyst i varje push/pull-svar.
 * Lyckat svar ar alltid 200, aven ett SYNC_PUSH-kuvert dar varje operation
 * foll; ErrorSpec.http_status galler nar HELA anropet faller, och ett
 * fallet helanrop ror aldrig utkorgen (se HELANROPSFEL i errors.ts).
 *
 * OPERATIONSKARTA
 *   Montorslage: SYNC_PULL (view 'worker'); FILE_UPLOAD + SYNC_PUSH 'put'
 *     for tid, mil, utlagg, foto; SYNC_PUSH 'week'/'submit'. "Mina
 *     arbetsordrar" ar en SORTERING i klienten, inte ett filter.
 *   Gastlage: samma vagar, view 'guest' — bara tilldelade ordrar, deras
 *     kunder och foton, och egna materialrader; annars UNKNOWN_WORK_ORDER.
 *   Chefslage: SYNC_PUSH 'put'/'delete' for stamdata, gasttilldelning,
 *     prislistor och installningar; TODAY; SYNC_PUSH 'week' for attest,
 *     las och aterppning; INVOICE_BASIS; SYNC_PUSH 'invoice_run';
 *     medlemsrutterna; UPDATE_COMPANY; TRANSFER_OWNERSHIP; SUBSCRIPTION /
 *     BILLING_CHECKOUT / BILLING_PORTAL / BILLING_PERIODS.
 *   Agaren, och ingen annan: TRANSFER_OWNERSHIP, BILLING_CHECKOUT,
 *     BILLING_PORTAL och DELETE_COMPANY — den sista oaterkallelig och tillaten
 *     aven i laslage, se rutten.
 *   Bokforare: samma lasvagar som chefen. Rollen 'bookkeeper' forekommer
 *     inte i EN ENDA regel i SYNC_RULES — sa ar beslut 07 genomdrivet, och
 *     att en rad inte heller kan AGAS av en bokforare tvingas av triggrar
 *     i schema/company.sql.
 *   Alla: AUTH_REQUEST_CODE/AUTH_VERIFY, ME, UPDATE_ACCOUNT, sync.
 *   Systemet: CLOSE_BILLING_PERIOD (pa varje firmas EGEN ankardag, utlost av
 *     invoice.created for ANKARFAKTURAN, av invoice.paid nar ankaret annars
 *     skulle stega over en ostangd period, och med dygnssvepet som
 *     efterslapsvakt), LOCK_EXPIRED_TRIALS (varje dygn),
 *     PURGE_CANCELLED_COMPANIES (varje dygn; raderar firmor som statt i
 *     laslage langre an DATA_RETENTION_DAYS), FORCE_TRANSFER_OWNERSHIP.
 *   Exporten ar INVOICE_BASIS; klienten skriver CSV:n. Montor och gast far
 *   FORBIDDEN — nagon prisfri form av underlaget finns inte.
 */

import type {
  AddMemberRequest,
  AuthRequestCodeRequest,
  AuthRequestCodeResponse,
  AuthVerifyRequest,
  AuthVerifyResponse,
  BillingCheckoutResponse,
  BillingPeriodsResponse,
  BillingPortalResponse,
  BinaryBody,
  CloseBillingPeriodRequest,
  CloseBillingPeriodResponse,
  CompanySummary,
  CreateCompanyRequest,
  CreateCompanyResponse,
  DeleteCompanyRequest,
  DeleteCompanyResponse,
  FileUploadRequest,
  FileUploadResponse,
  FullViewRole,
  InvoiceBasisRequest,
  InvoiceBasisResponse,
  LockExpiredTrialsRequest,
  LockExpiredTrialsResponse,
  MeResponse,
  MemberMutationResponse,
  PurgeCancelledCompaniesRequest,
  PurgeCancelledCompaniesResponse,
  RemoveMemberResponse,
  ReportingRole,
  Role,
  SetMemberRoleRequest,
  StripeWebhookRequest,
  StripeWebhookResponse,
  SubscriptionResponse,
  SyncDelete,
  SyncPullRequest,
  SyncPullResponse,
  SyncPushRequest,
  SyncPushResponse,
  SyncPut,
  TodayResponse,
  TransferOwnershipRequest,
  TransferOwnershipResponse,
  UpdateAccountRequest,
  UpdateCompanyRequest,
  VoiceTranscribeRequest,
  VoiceTranscribeResponse,
  WeekState,
  WeekTransition,
} from './types';

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'DELETE';
export type AuthLevel = 'public' | 'account' | 'company' | 'cron';
export type Idempotency = 'safe' | 'op_id' | 'per_op' | 'unsafe_no_op_id';
export type LockedMode = 'allowed' | 'blocked';
export type RequestIn = 'none' | 'query' | 'body';

export interface Route<TRequest, TResponse> {
  readonly method: HttpMethod;
  readonly path: string;
  readonly auth: AuthLevel;
  readonly roles: readonly Role[];
  /** true = bara agaren; en annan manager far OWNER_REQUIRED (beslut 14). */
  readonly requires_owner: boolean;
  readonly idempotency: Idempotency;
  readonly locked_mode: LockedMode;
  readonly request_in: RequestIn;
  /** Fantomfalt. Bar bara typen och finns aldrig i objektet. */
  readonly _request?: TRequest;
  readonly _response?: TResponse;
}

export type RequestOf<TRoute> = TRoute extends Route<infer R, unknown>
  ? R
  : never;
export type ResponseOf<TRoute> = TRoute extends Route<unknown, infer R>
  ? R
  : never;


export const REPORTING_ROLES: readonly ReportingRole[] = [
  'manager',
  'worker',
  'apprentice',
  'guest',
];

/** Ser hela firman inklusive priser: chefen och bokforaren (beslut 07). */
export const FULL_VIEW_ROLES: readonly FullViewRole[] = [
  'manager',
  'bookkeeper',
];

export const MANAGER_ONLY: readonly Role[] = ['manager'];

export const ALL_ROLES: readonly Role[] = [
  'manager',
  'worker',
  'apprentice',
  'guest',
  'bookkeeper',
];

export const NO_ROLES: readonly Role[] = [];

/* --- Konto och inloggning --- */

/**
 * Mejlar en sexsiffrig inloggningskod; skapar kontot om e-posten ar okand —
 * men aldrig utan namn (da VALIDATION_FAILED { field: 'name' }). Ofarlig att
 * upprepa: resultatet ar ytterligare ett mejl, aldrig ett andrat tillstand,
 * och inom samma tidsfonster ar det samma kod (den ar harledd, inte slumpad).
 * Svaret ar { sent: true } — aldrig koden, aldrig om kontot fanns.
 *
 * VAR EN LANK (AUTH_REQUEST_LINK, /api/auth/request-link) till 2026-09-11.
 * Bytet till kod ar iOS-problemet: en lank oppnas i Safari, som inte delar
 * lagring med appen pa hemskarmen, sa den installerade appen forblev
 * utloggad. Hela skalet star over AuthRequestCodeRequest i shared/types.ts.
 * Sokvagen ar ny med flit — den sager vad rutten gor.
 */
export const AUTH_REQUEST_CODE: Route<
  AuthRequestCodeRequest,
  AuthRequestCodeResponse
> = {
  method: 'POST',
  path: '/api/auth/request-code',
  auth: 'public',
  roles: NO_ROLES,
  requires_owner: false,
  idempotency: 'unsafe_no_op_id',
  locked_mode: 'allowed',
  request_in: 'body',
};

/**
 * Loser in koden: e-post + kod + enhet -> konto, kontobevis och licenser
 * (samma kropp som ME). Fel kod, utgangen kod och okant konto svarar ALLA
 * UNAUTHENTICATED. Sparren: LOGIN_CODE_MAX_ATTEMPTS forsok per adress och
 * minut, darefter RATE_LIMITED { retry_after_ms } — en miljon koder far
 * inte ga att prova igenom.
 */
export const AUTH_VERIFY: Route<AuthVerifyRequest, AuthVerifyResponse> = {
  method: 'POST',
  path: '/api/auth/verify',
  auth: 'public',
  roles: NO_ROLES,
  requires_owner: false,
  idempotency: 'unsafe_no_op_id',
  locked_mode: 'allowed',
  request_in: 'body',
};

/** Hamtar konto, firmor och farska bevis (beslut 12). */
export const ME: Route<void, MeResponse> = {
  method: 'GET',
  path: '/api/me',
  auth: 'account',
  roles: NO_ROLES,
  requires_owner: false,
  idempotency: 'safe',
  locked_mode: 'allowed',
  request_in: 'none',
};

export const UPDATE_ACCOUNT: Route<UpdateAccountRequest, MeResponse> = {
  method: 'PATCH',
  path: '/api/me',
  auth: 'account',
  roles: NO_ROLES,
  requires_owner: false,
  idempotency: 'op_id',
  locked_mode: 'allowed',
  request_in: 'body',
};

/* --- Firma och medlemmar (D1) --- */

/**
 * Skaparen blir agare (beslut 14) och manager i samma transaktion; servern
 * skapar aven DO:t, kor company.sql, speglar member-raden, lagger upp en
 * TOM standardprislista och skriver subscription-raden med status
 * 'trialing' — hela ordningen star pa CreateCompanyRequest.
 * Anropet avvisas med SUBSCRIPTION_LOCKED om kontot redan ar agare till en
 * firma vars status ar 'locked'; annars gick laslaget att lamna genom att
 * starta om.
 */
export const CREATE_COMPANY: Route<
  CreateCompanyRequest,
  CreateCompanyResponse
> = {
  method: 'POST',
  path: '/api/companies',
  auth: 'account',
  roles: NO_ROLES,
  requires_owner: false,
  idempotency: 'op_id',
  locked_mode: 'allowed',
  request_in: 'body',
};

export const UPDATE_COMPANY: Route<UpdateCompanyRequest, CompanySummary> = {
  method: 'PATCH',
  path: '/api/companies/:company_id',
  auth: 'company',
  roles: MANAGER_ONLY,
  requires_owner: false,
  idempotency: 'op_id',
  locked_mode: 'blocked',
  request_in: 'body',
};

/** Bjuder in en person. Gast kraver eget org.nr (beslut 17). */
export const ADD_MEMBER: Route<AddMemberRequest, MemberMutationResponse> = {
  method: 'POST',
  path: '/api/companies/:company_id/members',
  auth: 'company',
  roles: MANAGER_ONLY,
  requires_owner: false,
  idempotency: 'op_id',
  locked_mode: 'blocked',
  request_in: 'body',
};

export const SET_MEMBER_ROLE: Route<
  SetMemberRoleRequest,
  MemberMutationResponse
> = {
  method: 'PATCH',
  path: '/api/companies/:company_id/members/:account_id',
  auth: 'company',
  roles: MANAGER_ONLY,
  requires_owner: false,
  idempotency: 'op_id',
  locked_mode: 'blocked',
  request_in: 'body',
};

export const REMOVE_MEMBER: Route<void, RemoveMemberResponse> = {
  method: 'DELETE',
  path: '/api/companies/:company_id/members/:account_id',
  auth: 'company',
  roles: MANAGER_ONLY,
  requires_owner: false,
  idempotency: 'op_id',
  locked_mode: 'blocked',
  request_in: 'none',
};

/** Beslut 14: malkontot maste redan vara manager. Bara agaren far anropa. */
export const TRANSFER_OWNERSHIP: Route<
  TransferOwnershipRequest,
  TransferOwnershipResponse
> = {
  method: 'POST',
  path: '/api/companies/:company_id/owner',
  auth: 'company',
  roles: MANAGER_ONLY,
  requires_owner: true,
  idempotency: 'op_id',
  locked_mode: 'blocked',
  request_in: 'body',
};

/**
 * RADERAR FIRMAN. Oaterkalleligt — ingen papperskorg, ingen angerknapp, inget
 * som gar att lamna tillbaka. Firmans Durable Object toms och dess rader i D1
 * raderas; kaskaden i schema/d1.sql tar medlemskap, prenumeration,
 * fakturaperioder, kvitton, Stripe-handelser och firmans hela revisionshistorik.
 * Utan rutten gar loftet i villkoren punkt 10.6 och i
 * personuppgiftsbitradesavtalet punkt 12 bara att halla genom att nagon oppnar
 * databasen for hand.
 *
 * BARA AGAREN. requires_owner: true, sa routern svarar OWNER_REQUIRED at en
 * ANNAN manager — rollen racker till, det ar agarskapet som saknas — och
 * FORBIDDEN at montor, larling, gast och bokforare, som inte far gora saken
 * alls. Ingen supportvag och ingen FORCE-motsvarighet finns: en onabar agare
 * repareras med FORCE_TRANSFER_OWNERSHIP, och den nya agaren far sedan sjalv
 * bara ansvaret for att trycka.
 *
 * BEKRAFTELSEN AR FIRMANS ORG.NR I KROPPEN, provad AV SERVERN. Hela
 * motiveringen — varfor numret och inte namnet — star pa DeleteCompanyRequest
 * i shared/types.ts, tillsammans med den text klienten MASTE visa om att
 * exportera forst. Fel nummer ger VALIDATION_FAILED
 * { field: 'confirm_org_number' } och ingenting raderas.
 * Darfor request_in 'body' pa en DELETE: bekraftelsen far inte ligga i
 * sokvagen eller i en query, dar den hamnar i loggar och i webblasarhistorik —
 * och en DELETE utan kropp har ingen plats att bara den alls.
 *
 * EN FIRMA SOM FORTFARANDE BETALAR RADERAS INTE. Star prenumerationen i
 * 'active' eller 'past_due' med en tecknad prenumeration hos Stripe svarar
 * rutten ROW_IN_USE { table: 'company', id, blocked_by: 'subscription' } och
 * ingenting rors. Villkoret ar EXAKT komplementet till efterslapsvaktens
 * urval (CLOSE_PICK_SQL): en firma stangningen fortfarande kan valja far inte
 * kunna forsvinna under handerna pa den, och Stripe far aldrig fortsatta dra
 * pengar for nagot som inte finns. Ingen ny felkod: ROW_IN_USE:s blocked_by ar
 * per shared/errors.ts TABELLNAMNET som fortfarande pekar pa raden, och
 * subscription.company_id ar just det. Agaren sager upp i BILLING_PORTAL
 * forst — den ar oppen aven i laslage — och raderar sedan.
 *
 * locked_mode 'allowed', OCH DET AR ETT BESLUT. En last firma ar den FLITIGASTE
 * anroparen av den har rutten: laslaget ar vad en uppsagning leder till (punkt
 * 10.4), och punkt 10.7 lovar uttryckligen radering PA BEGARAN tidigare an de
 * tolv manaderna. Med 'blocked' hade det loftet gallt alla utom just dem det
 * skrevs for. Beslut 11 sager dessutom vad laslaget ar: inget NYTT kan skapas
 * eller andras — en radering skapar ingenting, andrar ingenting och kan inte
 * oka nagons skuld. Och en betalvagg framfor raderingen av personuppgifter vore
 * samma sort som en betalvagg framfor exporten, som samma beslut forbjuder.
 * Sparren ovan gor riktningen fullstandig: den som BETALAR maste saga upp
 * forst, den som ar LAST far radera direkt.
 */
export const DELETE_COMPANY: Route<DeleteCompanyRequest, DeleteCompanyResponse> = {
  method: 'DELETE',
  path: '/api/companies/:company_id',
  auth: 'company',
  roles: MANAGER_ONLY,
  requires_owner: true,
  idempotency: 'op_id',
  locked_mode: 'allowed',
  request_in: 'body',
};

/* --- Prenumeration: fungerar i laslage, annars kommer firman aldrig loss --- */

/**
 * Beslut 07: bokforaren ar UTOMSTAENDE och verksam i flera firmor, och han
 * star i FULL_VIEW_ROLES. Svaret bar darfor ALDRIG firmans
 * betalningsidentitet — de tre stripe_*-kolumnerna pa subscription
 * (customer, subscription, price); servern SELECTar bara kolumnerna i
 * SubscriptionPublicRow. Talet och priset far han se; handtagen som
 * administrerar eller debiterar kundforhallandet inte. Periodens fakturaid
 * ar en annan sak och gar ut i BILLING_PERIODS.
 * Det preliminara talet ar antal UPPLAGDA med betald roll just nu (beslut
 * 18), och nasta dragning laser klienten som current_period_end i samma
 * svar — firmans eget ankare, aldrig den 1:a.
 */
export const SUBSCRIPTION: Route<void, SubscriptionResponse> = {
  method: 'GET',
  path: '/api/companies/:company_id/subscription',
  auth: 'company',
  roles: FULL_VIEW_ROLES,
  requires_owner: false,
  idempotency: 'safe',
  locked_mode: 'allowed',
  request_in: 'none',
};

/**
 * Tecknar den FORSTA prenumerationen: skapar en Stripe Checkout-session och
 * returnerar dess url. Checkout ger Apple Pay och Google Pay och sparar
 * kortet for kommande dragningar. Portalen kan inte gora det — den
 * administrerar en befintlig prenumeration. Utan rutten kan en firma i
 * 'trialing' aldrig borja betala, och laslaget blir slutstationen.
 * ANKARET LAMNAS VIDARE HAR, MEN SKRIVS ALDRIG HAR (beslut 18): sessionen
 * skapar prenumerationen med trial_end = firmans current_period_end, sa att
 * STRIPES ankare blir FIRMANS ankare. Utan det borjar Stripes cykel den dag
 * kortet lades in — firman som signade den 20:e och betalar den 23:e skulle
 * da fa den 23:e som betalningsdag och dessutom betala for gratisveckan.
 * Rutten LASER alltsa current_period_end; den skriver ALDRIG
 * current_period_start eller current_period_end. Kolumnerna har exakt tva
 * skrivare: CREATE_COMPANY en gang, invoice.paid darefter.
 * OMTECKNING EFTER ETT LAS ar undantaget i sessionens utformning, inte i
 * skrivregeln: da ligger det gamla ankaret i det FORFLUTNA, och ett passerat
 * trial_end avvisas av Stripe. Sessionen skapas darfor UTAN trial_end, Stripe
 * drar direkt, och det ar den forsta invoice.paid som skriver firmans nya
 * ankare. Rutten satter det inte sjalv — annars finns tva skrivare till samma
 * dag, precis det som forbjuds pa subscription i schema/d1.sql.
 * BILLING_PORTAL far inte oppnas med mojligheten att andra betalningsdag:
 * ankaret gar inte att byta for en lopande prenumeration (se subscription i
 * schema/d1.sql). Omteckning efter ett las ar enda vagen till ett nytt.
 */
export const BILLING_CHECKOUT: Route<void, BillingCheckoutResponse> = {
  method: 'POST',
  path: '/api/companies/:company_id/subscription/checkout',
  auth: 'company',
  roles: MANAGER_ONLY,
  requires_owner: true,
  idempotency: 'op_id',
  locked_mode: 'allowed',
  request_in: 'none',
};

export const BILLING_PORTAL: Route<void, BillingPortalResponse> = {
  method: 'POST',
  path: '/api/companies/:company_id/subscription/portal',
  auth: 'company',
  roles: MANAGER_ONLY,
  requires_owner: true,
  idempotency: 'op_id',
  locked_mode: 'allowed',
  request_in: 'none',
};

/**
 * De stangda ankarperioderna: granserna (period_start / period_end), antal,
 * belopp och — med flit — Stripes fakturaid. Det ar det ENDA Stripe-id som
 * lamnar servern (BillingPeriodRow, beslut 07 och SubscriptionPublicRow):
 * det pekar ut en enskild faktura, inte firmans kundforhallande, och
 * bokforaren i FULL_VIEW_ROLES ska kunna stamma av Klartids period mot
 * bokforingen utan att na BILLING_PORTAL, som ar MANAGER_ONLY och kraver
 * agaren. Sekretessregeln i SubscriptionPublicRow omfattar alltsa tre
 * namngivna kolumner, inte varje strang som borjar pa 'stripe_' — annars
 * skulle den och den har rutten saga emot varandra.
 */
export const BILLING_PERIODS: Route<void, BillingPeriodsResponse> = {
  method: 'GET',
  path: '/api/companies/:company_id/billing-periods',
  auth: 'company',
  roles: FULL_VIEW_ROLES,
  requires_owner: false,
  idempotency: 'safe',
  locked_mode: 'allowed',
  request_in: 'none',
};

/**
 * Stripes webhook. auth 'public' — signaturen (Stripe-Signature) mot den
 * ORORDA kroppen ar behorighetskontrollen; faller den svarar rutten 400 och
 * ingenting skrivs.
 * IDEMPOTENSEN LIGGER I stripe_event, INTE I ETT op_id. Rutten ar
 * 'unsafe_no_op_id' darfor att Stripe inte satter X-Op-Id, sa den kan inte
 * spela upp ett svar ur account_op_log. STRIPE LEVERERAR OM: samma evt_id
 * kan komma flera ganger (timeout, 5xx, omleverans fran instrumentpanelen),
 * och for invoice.created skulle en omleverans starta stangningen en gang
 * till.
 * DARFOR SKRIVS EVENT-ID:T FORST, med INSERT OR IGNORE och i SAMMA
 * transaktion som handelsen behandlas. Andrades noll rader ar handelsen
 * redan sedd: svaret ar 200 och ingenting mer hander. Fallerar efterarbetet
 * rullas hela transaktionen tillbaka, event-raden inrymd, och rutten svarar
 * 5xx sa att Stripe levererar om — annars ligger id:t kvar som "sett" for en
 * handelse som aldrig utfordes. Det enda som inte ryms i transaktionen ar
 * skrivningen HOS STRIPE (stangningens engangspost), och den bar sin egen
 * idempotensnyckel 'klartid:close:<company_id>:<period_start>'.
 * VAD VARJE HANDELSE GOR star i handelsekartan pa subscription i
 * schema/d1.sql, inklusive AVGRANSNINGEN (bara ankarfakturan utloser
 * CLOSE_BILLING_PERIOD), MONOTONICITETEN (ankaret gar bara framat) och STANG
 * FORE FLYTT (ankaret stegar aldrig over en ostangd period). Det ar samma
 * kanal som bar bade ankaret och pengarna, sa ingen av de reglerna far
 * lamnas till en implementation att gissa.
 */
export const STRIPE_WEBHOOK: Route<
  StripeWebhookRequest,
  StripeWebhookResponse
> = {
  method: 'POST',
  path: '/api/stripe/webhook',
  auth: 'public',
  roles: NO_ROLES,
  requires_owner: false,
  idempotency: 'unsafe_no_op_id',
  locked_mode: 'allowed',
  request_in: 'body',
};

/* --- Synk: den enda skrivkanalen mot foretagsdata --- */

/**
 * Batchad utkorg. Varje operation bar sitt op_id och avgors for sig; en
 * fallen operation ger inte fel pa resten, och varje operation kors i en
 * egen transaktion tillsammans med sin kvittorad.
 * Ett giltigt kuvert svarar ALLTID 200; utfallet per operation star i
 * SyncOpResult. Hela anropet faller bara pa UNAUTHENTICATED,
 * LICENSE_EXPIRED, FORBIDDEN, SUBSCRIPTION_LOCKED, TOO_MANY_OPS,
 * VALIDATION_FAILED (dubblerat op_id i kuvertet), RATE_LIMITED,
 * SERVER_ERROR eller SERVICE_UNAVAILABLE — och da ligger varje operation
 * kvar i utkorgen orord.
 * roles ar ALL_ROLES for att slippa tva sorters avslag pa samma anrop; vad
 * var och en far gora avgors per operation i SYNC_RULES.
 */
export const SYNC_PUSH: Route<SyncPushRequest, SyncPushResponse> = {
  method: 'POST',
  path: '/api/companies/:company_id/sync/push',
  auth: 'company',
  roles: ALL_ROLES,
  requires_owner: false,
  idempotency: 'per_op',
  locked_mode: 'blocked',
  request_in: 'body',
};

/**
 * Hamtar allt som andrats sedan markoren. Beslut 16 och 17: svaret ar en
 * diskriminerad union pa view, och vyn foljer av rollen I D1 enligt
 * ROLE_VIEW. Markorens innebord (next_since = hogsta LASTA seq) och regeln
 * om vad klienten gor nar view byter varde star pa SyncPullResponse — bada
 * maste lasas fore steg 5. Klienten kor alltid en pull direkt efter en
 * lyckad push.
 */
export const SYNC_PULL: Route<SyncPullRequest, SyncPullResponse> = {
  method: 'GET',
  path: '/api/companies/:company_id/sync/pull',
  auth: 'company',
  roles: ALL_ROLES,
  requires_owner: false,
  idempotency: 'safe',
  locked_mode: 'allowed',
  request_in: 'query',
};

/* --- Vyer --- */

export const TODAY: Route<void, TodayResponse> = {
  method: 'GET',
  path: '/api/companies/:company_id/today',
  auth: 'company',
  roles: FULL_VIEW_ROLES,
  requires_owner: false,
  idempotency: 'safe',
  locked_mode: 'allowed',
  request_in: 'none',
};

/**
 * Beslut 05: attesterat och oattesterat var for sig, utan gemensam total.
 * Beslut 11: fungerar i laslage — export ligger aldrig bakom betalvagg.
 * Klienten gor CSV:n ur svaret; nagon separat exportvag behovs inte.
 */
export const INVOICE_BASIS: Route<InvoiceBasisRequest, InvoiceBasisResponse> = {
  method: 'GET',
  path: '/api/companies/:company_id/invoice-basis',
  auth: 'company',
  roles: FULL_VIEW_ROLES,
  requires_owner: false,
  idempotency: 'safe',
  locked_mode: 'allowed',
  request_in: 'query',
};

/* --- Filer och rost --- */

export const FILE_UPLOAD: Route<FileUploadRequest, FileUploadResponse> = {
  method: 'POST',
  path: '/api/companies/:company_id/files',
  auth: 'company',
  roles: REPORTING_ROLES,
  requires_owner: false,
  idempotency: 'op_id',
  locked_mode: 'blocked',
  request_in: 'body',
};

/**
 * Nedladdning fungerar i laslage. TVA KONTROLLER, bada obligatoriska —
 * ogissbarhet ar aldrig behorighetskontroll:
 *   1. Nyckelns forsta segment MASTE vara sokvagens :company_id, annars
 *      UNKNOWN_REFERENCE { table: 'file', id }. Det hindrar en bokforare i
 *      bade A och B fran att hamta B:s filer genom A:s sokvag.
 *   2. Servern slar upp nyckeln i firmans DO fore utlamning: den far
 *      hamtas om den sitter som work_order_photo.object_key pa en levande
 *      rad anroparen far se enligt sin ClientView, eller som
 *      expense.receipt_object_key pa en utlaggsrad han far lasa (montors-
 *      och gastlage: bara egna). Annars samma UNKNOWN_REFERENCE. Ett
 *      kvittofoto visar belopp och inkop, och SYNC_PULL slapper med flit
 *      bara ut en montors egna utlagg.
 */
export const FILE_DOWNLOAD: Route<void, BinaryBody> = {
  method: 'GET',
  path: '/api/companies/:company_id/files/:object_key',
  auth: 'company',
  roles: ALL_ROLES,
  requires_owner: false,
  idempotency: 'safe',
  locked_mode: 'allowed',
  request_in: 'none',
};

export const VOICE_TRANSCRIBE: Route<
  VoiceTranscribeRequest,
  VoiceTranscribeResponse
> = {
  method: 'POST',
  path: '/api/companies/:company_id/voice/transcribe',
  auth: 'company',
  roles: REPORTING_ROLES,
  requires_owner: false,
  idempotency: 'unsafe_no_op_id',
  locked_mode: 'blocked',
  request_in: 'body',
};

/* --- Systemets egna rutter (auth 'cron', aldrig natbara utifran) --- */

/**
 * PERIODSTANGNINGEN (beslut 18). Anropas EN GANG PER FIRMA OCH ANKARE — det
 * finns ingen gemensam stangning den 1:a, for varje firma har sin egen
 * betalningsdag.
 * NORMALVAGEN AR invoice.created-webhooken: Stripe skapar abonnemangsfakturan
 * i ankarogonblicket och vantar ~1 h innan den slutfors, och engangsposten
 * maste ligga dar innan dess for att hamna pa DEN fakturan i stallet for
 * nasta cykels. BARA ANKARFAKTURAN utloser stangningen — en manuell faktura
 * eller en efterslapande post ger ocksa invoice.created, och de kvitteras 200
 * och ror ingenting (AVGRANSNINGEN pa subscription i schema/d1.sql).
 * En Cron Trigger sveper dessutom VARJE DYGN som EFTERSLAPSVAKT; svepet kan
 * ligga upp till ett dygn efter ankaret och stanger aldrig i forvag.
 * Villkoret som valjer ut firmorna star ordagrant som en fardig fraga pa
 * billing_period i schema/d1.sql och har TVA grenar, for ankaret star inte
 * alltid kvar och vantar: (a) ankaret (current_period_end) har passerat utan
 * att perioden stangts, och (b) invoice.paid har REDAN flyttat ankaret utan
 * att den passerade perioden fick en rad — da ar period_start
 * current_period_start i stallet. Utan (b) ar vakten dod i exakt det fall
 * den finns for: en betalande firmas ankarfaktura blir 0 kr utan
 * engangsposten, en nollfaktura betalas direkt, och ankaret star en manad
 * fram langt innan svepet kor. Fragan svarar darfor med BADE company_id och
 * det period_start rutten ska anropas med.
 * Rutten raknar antalet UPPLAGDA personer med en betald roll (PAID_ROLES) i
 * firmans DO — hela fragan ar ett COUNT, inga rader, ingen rapportering,
 * ingen proportionering — fryser talet i billing_period och lagger upp
 * periodens post hos Stripe som EN ENGANGSPOST med hela klumpsumman (formen
 * star pa subscription i schema/d1.sql och far inte gissas om). Talet
 * Klartid mater AR det kunden debiteras; Stripe kan bara gora fakturan lagre
 * (rabatt, kvarvarande provperiod), aldrig hogre. op_id harleds
 * deterministiskt ur period_start (se CloseBillingPeriodRequest) sa att en
 * omkorning blir 'duplicate'.
 * RUTTEN ROR STRIPE I BADA RIKTNINGARNA: period_end finns inte i begaran utan
 * LASES ur abonnemangsradens period.end pa ankarets faktura innan raden
 * skrivs (se CloseBillingPeriodRequest). Gar den lasningen inte att gora
 * stangs ingen period, och firman tas i nasta svep.
 * RUTTEN LASER INGA PROVPERIODER: den ror bara firmor som redan har en
 * tecknad prenumeration, sa en firma som aldrig lade in ett kort passerar
 * den obemarkt — det ar LOCK_EXPIRED_TRIALS som avslutar den provperioden.
 * Utan rutten skapas firmor som aldrig faktureras.
 */
export const CLOSE_BILLING_PERIOD: Route<
  CloseBillingPeriodRequest,
  CloseBillingPeriodResponse
> = {
  method: 'POST',
  path: '/api/internal/billing/close',
  auth: 'cron',
  roles: NO_ROLES,
  requires_owner: false,
  idempotency: 'op_id',
  locked_mode: 'allowed',
  request_in: 'body',
};

/**
 * Provperiodssvepet (TRIAL_DAYS mot beslut 11). Cron Trigger VARJE DYGN:
 * varje firma som star i 'trialing' med passerad current_period_end satts
 * till 'locked'. TRIAL_DAYS ar 7 raknat fran signeringsdagen, sa
 * provperioder loper ut pa alla veckans dagar — ingen fast dag i kalendern
 * kan fanga dem.
 * SVEPET AR ENDA VAGEN UT UR EN OBETALD PROVPERIOD. CLOSE_BILLING_PERIOD kan
 * inte gora jobbet: den ror bara firmor som redan har en tecknad
 * prenumeration, och en firma som aldrig gjorde BILLING_CHECKOUT har varken
 * cykel eller ankare att stanga. De tva rutterna delar alltsa ingen regel
 * langre — de har varsin uppgift, och bara den har rutten ror status
 * 'trialing'.
 * Rent idempotent (en firma som redan ar 'locked' ror svepet inte) och
 * skriver audit_log 'subscription.status_changed' med actor_account_id NULL.
 */
export const LOCK_EXPIRED_TRIALS: Route<
  LockExpiredTrialsRequest,
  LockExpiredTrialsResponse
> = {
  method: 'POST',
  path: '/api/internal/billing/lock-expired-trials',
  auth: 'cron',
  roles: NO_ROLES,
  requires_owner: false,
  idempotency: 'op_id',
  locked_mode: 'allowed',
  request_in: 'body',
};

/**
 * GALLRINGEN (villkoren punkt 10.5 och 10.6). Cron Trigger VARJE DYGN: varje
 * firma som statt i laslage langre an DATA_RETENTION_DAYS raderas, med exakt
 * samma verkan som DELETE_COMPANY.
 * URVALET star ordagrant pa company i schema/d1.sql och har ETT villkor med
 * tre delar: status 'locked', ett locked_at som finns, och ett locked_at som
 * gatt ut. EN FIRMA SOM FORTFARANDE KAN KOMMA TILLBAKA GALLRAS ALDRIG —
 * 'trialing' ar en provperiod som annu inte tagit slut, 'active' betalar,
 * 'past_due' ar en misslyckad dragning i respit, och en firma som tecknat om
 * enligt punkt 9.3 lamnar 'locked' och far locked_at NULL i samma sats som
 * statusen. Uppsagningsdagen bor i den kolumnen och ingen annanstans.
 * Rent idempotent pa tva satt: op_id harleds ur dagen (SHA-256
 * 'purge-sweep:' + date), sa en andra korning samma dygn spelas upp ur
 * account_op_log; och en firma som redan raderats finns inte kvar att valja.
 * En firma vars Durable Object inte gick att tomma lamnas ORORD och tas i
 * nasta svep — D1-raden bar den enda pekaren (do_id) till uppgifterna, och
 * raderas den forst gar de aldrig mer att na eller radera.
 * Skriver audit_log 'company.purged' med actor_account_id NULL och
 * company_id NULL; firmans id ligger i detail, for kolumnen bar
 * ON DELETE CASCADE och en rad som pekade pa firman hade forsvunnit med den.
 */
export const PURGE_CANCELLED_COMPANIES: Route<
  PurgeCancelledCompaniesRequest,
  PurgeCancelledCompaniesResponse
> = {
  method: 'POST',
  path: '/api/internal/companies/purge-cancelled',
  auth: 'cron',
  roles: NO_ROLES,
  requires_owner: false,
  idempotency: 'op_id',
  locked_mode: 'allowed',
  request_in: 'body',
};

/**
 * Supportvagen nar agaren ar onabar. Agaren kan inte tas bort eller
 * degraderas medan han ar agare (triggrar i schema/d1.sql), och
 * TRANSFER_OWNERSHIP kraver att han sjalv anropar den — slutar han eller
 * gar bort finns annars ingen vag som reparerar firman: ingen kan na
 * BILLING_PORTAL och ingen kan overlata. Malkontot maste anda redan vara
 * manager. Skriver audit_log 'company.ownership_transferred' med den nya
 * agaren som actor och arendet i detail.
 */
export const FORCE_TRANSFER_OWNERSHIP: Route<
  TransferOwnershipRequest,
  TransferOwnershipResponse
> = {
  method: 'POST',
  path: '/api/internal/companies/:company_id/owner',
  auth: 'cron',
  roles: NO_ROLES,
  requires_owner: false,
  idempotency: 'op_id',
  locked_mode: 'allowed',
  request_in: 'body',
};

/* --- Hela tabellen --- */

export const ROUTES = [
  AUTH_REQUEST_CODE,
  AUTH_VERIFY,
  ME,
  UPDATE_ACCOUNT,
  CREATE_COMPANY,
  UPDATE_COMPANY,
  ADD_MEMBER,
  SET_MEMBER_ROLE,
  REMOVE_MEMBER,
  TRANSFER_OWNERSHIP,
  DELETE_COMPANY,
  SUBSCRIPTION,
  BILLING_CHECKOUT,
  BILLING_PORTAL,
  BILLING_PERIODS,
  STRIPE_WEBHOOK,
  SYNC_PUSH,
  SYNC_PULL,
  TODAY,
  INVOICE_BASIS,
  FILE_UPLOAD,
  FILE_DOWNLOAD,
  VOICE_TRANSCRIBE,
  CLOSE_BILLING_PERIOD,
  LOCK_EXPIRED_TRIALS,
  PURGE_CANCELLED_COMPANIES,
  FORCE_TRANSFER_OWNERSHIP,
] as const;

export type AnyRoute = (typeof ROUTES)[number];

/* Veckans tillstandsmaskin som DATA (beslut 04, 10 och 19). Rollerna star
 * i SYNC_RULES ('week:submit' m.fl.), sa att de finns pa ETT stalle.
 * EN OVERGANG UTANFOR TABELLEN ger WEEK_TRANSITION_INVALID
 * { iso_week, state }, dar state ar veckans verkliga lage — aldrig
 * VALIDATION_FAILED, som per shared/errors.ts alltid bar { field } och
 * betyder att ett namngivet falt i begaran ar felskrivet. Begaran ar
 * valformad; det ar veckan som star nagon annanstans. */

export interface WeekTransitionRule {
  readonly from: readonly WeekState[];
  readonly to: WeekState;
}

export const WEEK_TRANSITIONS = {
  /**
   * Personen lamnar in. 'submitted' ar med bland fran-tillstanden:
   * EDITABLE_WEEK_STATES tillater andring i en redan inlamnad vecka, sa den
   * som rattat en rad maste kunna trycka "Lamna in" igen — ett tillatet
   * no-op som bara bumpar version och seq.
   */
  submit: { from: ['open', 'submitted', 'reopened'], to: 'submitted' },
  /**
   * Chefen attesterar. 'open' ar med for att en vecka annars kunde lasas
   * for alltid: den som gar pa semester eller slutar utan att lamna in har
   * timmar som aldrig kan attesteras, aldrig stamplas av en invoice_run och
   * darfor aldrig faktureras. 'reopened' ar med sa att beslut 19 gar runt.
   */
  approve: { from: ['open', 'submitted', 'reopened'], to: 'approved' },
  lock: { from: ['approved'], to: 'locked' },
  reopen: { from: ['approved', 'locked'], to: 'reopened' },
} as const satisfies Record<WeekTransition, WeekTransitionRule>;

/**
 * BESLUT 10 som data: nar en manager sparar en rad DAR HAN SJALV AR
 * account_id och hans egen vecka star i 'open', satter servern veckan
 * direkt till 'approved' — hans tid gar aldrig genom attestkon.
 * Automatiken utloses BARA fran 'open' och BARA for hans egna rader; en rad
 * han skriver at nagon annan lamnar den personens vecka orord. Att veckan
 * darefter forblir SKRIVBAR for honom foljer av
 * MANAGER_OWN_WEEK_WRITABLE_STATES.
 */
export const MANAGER_WEEK_AUTO_APPROVE = {
  role: 'manager',
  applies_to: 'own_rows_only',
  from: 'open',
  to: 'approved',
} as const;

/* Rollregler per synkoperation. SYNC_PUSH slapper in alla roller; den har
 * tabellen avgor vad var och en far gora med en ENSKILD operation.
 *   roles        roller som far utfora operationen
 *   week_locked  'blocked' = operationen ror en rad som hor till en vecka
 *                och gar bara igenom om veckan star i EDITABLE_WEEK_STATES,
 *                eller — om veckans agare ar den inloggade OCH han ar
 *                manager — i MANAGER_OWN_WEEK_WRITABLE_STATES. Annars
 *                WEEK_LOCKED. En rad som bar en invoice_run_id ar dessutom
 *                last for alltid: ROW_INVOICED. 'n/a' = ingen vecka.
 *   actor_scope  'self_only' | 'self_or_manager' (worker, apprentice och
 *                guest bara sina egna rader; manager vems som helst) | 'any'
 * UTOVER DETTA galler tva regler ur schema/company.sql som ingen rollmangd
 * kan ersatta: en rad kan aldrig agas av en bokforare (beslut 07, triggrar),
 * och en GAST far bara rora arbetsordrar han tilldelats, ar ansvarig for
 * eller redan har egna rader pa (beslut 17) — annars UNKNOWN_WORK_ORDER.
 * Bokforaren forekommer inte i EN ENDA rad nedan.
 *
 * KONTROLLORDNING I EN OPERATION, ordagrant och i denna foljd:
 *   1. roles             rollregeln nedan                    FORBIDDEN
 *   2. actor_scope       vems rad det ar                     FORBIDDEN
 *   3. base_version      versionsjamforelsen                 SYNC_CONFLICT
 *   4. invoice_run_id    raden ar redan fakturerad           ROW_INVOICED
 *   5. referensuppslag   arbetsorder, kund, moment, member,
 *                        prislista, fil                      UNKNOWN_WORK_ORDER
 *                                                            / UNKNOWN_REFERENCE
 *   6. veckoregeln       BADA veckorna vid en flytt          WEEK_LOCKED
 *   7. pris              uppslaget i prislistan              PRICE_MISSING
 *   8. skrivningen       ett UNIQUE-index som brister        ROW_DUPLICATE
 * ORDNINGEN AR EN SEKRETESSGRANS, inte en smaksak: actor_scope MASTE provas
 * FORE uppslaget av arbetsordern (steg 5), annars far en gast FORBIDDEN i
 * stallet for UNKNOWN_WORK_ORDER och far darmed veta att en order han inte
 * ar tilldelad existerar — precis det beslut 17 forbjuder. Att
 * versionsjamforelsen star fore ROW_INVOICED foljer av att en stampling
 * aldrig andrar version: en telefon som legat offline medan chefen
 * exporterade ska fa ROW_INVOICED, aldrig SYNC_CONFLICT. */

export type ActorScope = 'self_only' | 'self_or_manager' | 'any';

export interface SyncRule {
  readonly roles: readonly Role[];
  readonly week_locked: 'blocked' | 'n/a';
  readonly actor_scope: ActorScope;
}

/**
 * Nyckeln harleds ur OPERATIONSUNIONEN, aldrig ur SyncTable: 'invoice_run'
 * finns inte i SyncPut och 'company_settings' inte i SyncDelete, sa
 * 'put:invoice_run' och 'delete:company_settings' vore regler som ingen
 * giltig operation kan traffa. Som NO_ROLES sag de ut som ett skydd men var
 * dod kod — och den dag nagon lade till operationen hade han trott att
 * regeln redan gallde. Skapandet av en invoice_run styrs av den enda
 * nyckeln utanfor monstret, 'invoice_run:create'.
 */
export type SyncRuleKey =
  | `put:${SyncPut['table']}`
  | `delete:${SyncDelete['table']}`
  | `week:${WeekTransition}`
  | 'invoice_run:create';

export const SYNC_RULES = {
  'put:customer': { roles: MANAGER_ONLY, week_locked: 'n/a', actor_scope: 'any' },
  'delete:customer': { roles: MANAGER_ONLY, week_locked: 'n/a', actor_scope: 'any' },
  'put:work_order': { roles: MANAGER_ONLY, week_locked: 'n/a', actor_scope: 'any' },
  'delete:work_order': { roles: MANAGER_ONLY, week_locked: 'n/a', actor_scope: 'any' },
  'put:task': { roles: MANAGER_ONLY, week_locked: 'n/a', actor_scope: 'any' },
  'delete:task': { roles: MANAGER_ONLY, week_locked: 'n/a', actor_scope: 'any' },

  'put:work_order_guest': { roles: MANAGER_ONLY, week_locked: 'n/a', actor_scope: 'any' },
  'delete:work_order_guest': { roles: MANAGER_ONLY, week_locked: 'n/a', actor_scope: 'any' },

  'put:price_list': { roles: MANAGER_ONLY, week_locked: 'n/a', actor_scope: 'any' },
  'delete:price_list': { roles: MANAGER_ONLY, week_locked: 'n/a', actor_scope: 'any' },
  'put:price_rate': { roles: MANAGER_ONLY, week_locked: 'n/a', actor_scope: 'any' },
  'delete:price_rate': { roles: MANAGER_ONLY, week_locked: 'n/a', actor_scope: 'any' },
  'put:company_settings': { roles: MANAGER_ONLY, week_locked: 'n/a', actor_scope: 'any' },

  'put:work_order_photo': { roles: REPORTING_ROLES, week_locked: 'n/a', actor_scope: 'any' },
  'delete:work_order_photo': { roles: MANAGER_ONLY, week_locked: 'n/a', actor_scope: 'any' },

  'put:time_entry': { roles: REPORTING_ROLES, week_locked: 'blocked', actor_scope: 'self_or_manager' },
  'delete:time_entry': { roles: REPORTING_ROLES, week_locked: 'blocked', actor_scope: 'self_or_manager' },
  'put:material_line': { roles: REPORTING_ROLES, week_locked: 'blocked', actor_scope: 'self_or_manager' },
  'delete:material_line': { roles: REPORTING_ROLES, week_locked: 'blocked', actor_scope: 'self_or_manager' },
  'put:mileage': { roles: REPORTING_ROLES, week_locked: 'blocked', actor_scope: 'self_or_manager' },
  'delete:mileage': { roles: REPORTING_ROLES, week_locked: 'blocked', actor_scope: 'self_or_manager' },
  'put:expense': { roles: REPORTING_ROLES, week_locked: 'blocked', actor_scope: 'self_or_manager' },
  'delete:expense': { roles: REPORTING_ROLES, week_locked: 'blocked', actor_scope: 'self_or_manager' },
  'put:absence': { roles: REPORTING_ROLES, week_locked: 'blocked', actor_scope: 'self_or_manager' },
  'delete:absence': { roles: REPORTING_ROLES, week_locked: 'blocked', actor_scope: 'self_or_manager' },

  'delete:invoice_run': { roles: MANAGER_ONLY, week_locked: 'n/a', actor_scope: 'any' },
  'invoice_run:create': { roles: MANAGER_ONLY, week_locked: 'n/a', actor_scope: 'any' },

  // Veckans tillstand (beslut 04, 10, 19); fran/till star i WEEK_TRANSITIONS.
  'week:submit': { roles: REPORTING_ROLES, week_locked: 'n/a', actor_scope: 'self_only' },
  'week:approve': { roles: MANAGER_ONLY, week_locked: 'n/a', actor_scope: 'any' },
  'week:lock': { roles: MANAGER_ONLY, week_locked: 'n/a', actor_scope: 'any' },
  'week:reopen': { roles: MANAGER_ONLY, week_locked: 'n/a', actor_scope: 'any' },
} as const satisfies Record<SyncRuleKey, SyncRule>;
